import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Cards } from '@sorare/core/src/assets/icons/Cards';
import { SportBall } from '@sorare/core/src/atoms/icons/SportBall';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/lib/gql';
import { BasicBenchExtraWrapper } from '@sorare/marketplace/src/components/searchPlayerTable/BasicBenchExtraWrapper';

import { BenchExtra } from '#src/components/compose/BenchExtra/index.tsx';

import type { OwnedCardFooter_anyCardInterface } from './__generated__/index.graphql.ts';

const StyledLabel = styled(Horizontal).attrs({
  gap: 0.5,
  center: true,
})`
  font: var(--t-label-xs);
`;

const ExtraFooter = ({
  rgbColor,
  children,
}: {
  rgbColor: string;
  children: ReactNode;
}) => {
  return (
    <div
      style={
        {
          '--footer-bg-color': `rgba(${rgbColor}, 0.16)`,
          '--color': `rgba(${rgbColor}, 1)`,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
};

export const OwnedCardFooter = ({
  card,
  taskId,
  showOwnedLabel = true,
}: {
  card: OwnedCardFooter_anyCardInterface;
  taskId: string;
  showOwnedLabel?: boolean;
}) => {
  const alreadyPlayed =
    isType(card.usedIn, 'UsedInTasks') &&
    card.usedIn.tasks.filter(task => task.id !== taskId).length > 0;
  const listOnMarket = isType(card.usedIn, 'ListedOnMarket');
  const playedInPro =
    isType(card.usedIn, 'UsedInSo5Lineups') &&
    card.usedIn.so5Lineups.length > 0;

  const content = (
    <BenchExtra
      anyCard={card}
      anyPlayer={card.anyPlayer}
      suspensions={card.anyPlayer.activeSuspensions}
      hasMultipleGames={false}
      taskId={taskId}
      showInjury={false}
      showSuspension={false}
    />
  );
  if (alreadyPlayed) {
    return <ExtraFooter rgbColor="var(--c-rgb-red-400)">{content}</ExtraFooter>;
  }
  if (playedInPro) {
    return (
      <ExtraFooter rgbColor="var(--c-rgb-green-400)">{content}</ExtraFooter>
    );
  }
  if (listOnMarket) {
    return (
      <ExtraFooter rgbColor="var(--c-rgb-blue-300)">{content}</ExtraFooter>
    );
  }
  if (
    isType(card.usedIn, 'UsedInTasks') &&
    card.usedIn.tasks.filter(task => task.id === taskId).length > 0
  ) {
    return (
      <ExtraFooter rgbColor="var(--c-rgb-blue-300)">
        <BasicBenchExtraWrapper>
          <SportBall sport={card.sport} size={1.5} />
          <StyledLabel>
            <FormattedMessage
              id="TaskCompose.Selected"
              defaultMessage="Selected"
            />
          </StyledLabel>
        </BasicBenchExtraWrapper>
      </ExtraFooter>
    );
  }
  if (showOwnedLabel) {
    return (
      <ExtraFooter rgbColor="var(--c-rgb-score-high)">
        <BasicBenchExtraWrapper>
          <Cards size={1.5} />
          <StyledLabel>
            <FormattedMessage
              id="TaskCompose.OwnerCardFooter"
              defaultMessage="Owned"
            />
          </StyledLabel>
        </BasicBenchExtraWrapper>
      </ExtraFooter>
    );
  }
  return null;
};

OwnedCardFooter.fragments = {
  anyCardInterface: gql`
    fragment OwnedCardFooter_anyCardInterface on AnyCardInterface {
      slug
      sport
      anyPlayer {
        slug
        ...BenchExtra_anyPlayerInterface
        activeSuspensions {
          id
          ...BenchExtra_suspension
        }
      }
      usedIn(
        so5LeaderboardSlug: $so5LeaderboardSlug
        taskId: $taskId
        gameId: $gameId
      ) {
        slug
        ... on UsedInTasks {
          slug
          tasks {
            id
          }
        }
        ... on ListedOnMarket {
          slug
        }
        ... on UsedInSo5Lineups {
          slug
          so5Lineups {
            id
          }
        }
      }
      ...BenchExtra_anyCardInterface
    }
    ${BenchExtra.fragments.anyCard}
    ${BenchExtra.fragments.anyPlayer}
    ${BenchExtra.fragments.suspension}
  ` as TypedDocumentNode<OwnedCardFooter_anyCardInterface>,
};
